import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Alert, AlertDescription, AlertIcon, AlertTitle, Box, Button, Flex, Text } from "@chakra-ui/react";
import { Header } from "../../components/Header";
import { Limites } from "../Pdv/LImites";
import { Loading } from "../../components/Loading";
import { formatBRL, isRs } from "../../utils/currency";
import { usePedidoDetalhes } from "../../hooks/pagamento/usePedidoDetalhes";
import { MdFileDownload } from "react-icons/md";
import { HiArrowUturnLeft, HiTicket } from "react-icons/hi2";
import { IoTicketSharp } from "react-icons/io5";
import { BilheteStatusCode, DocumentoPreferencial } from "../../utils/interfaces";
import { abrirCartaoPdf, abrirVoucherPdf } from "../../libs/axios";

export function CompraFinalizada() {
  const navigate = useNavigate()

  const { id } = useParams() as { id: string };
  const { data, isLoading, isError } = usePedidoDetalhes({ id });

  useEffect(() => {
    if (isError) {
      navigate('/');
    }
  }, [isError, navigate]);

  if (isLoading) return <Loading />;

  if (typeof data === "undefined") return <Loading />;

  return (
    <Box
      display={"flex"}
      flexDir={"column"}
      justifyContent={"center"}
      width={"full"}
      alignItems={"center"}
    >
      <Flex
        maxWidth={"140rem"}
        width={"100%"}
        flexDir={"column"}
        justifyContent={"center"}
      >
        <Header />
        <Box
          px={"1rem"}
          bg={"white"}
          w={"full"}
          display={"flex"}
          flexDir={"column"}
          gap={"8"}
          pb={8}
        >
          <Limites />

          <Box bg={"#f4f4f4"}  p={"4"} display={"flex"} flexDir={"column"}>
            <Text fontSize={"lg"} fontWeight={"900"}>
              CONFIRMAÇÃO
            </Text>
            <Text textAlign={"center"} fontSize={"7xl"} color={"green.400"}>
              PEDIDO {id} EFETUADO COM SUCESSO
            </Text>
            <Text textAlign={"center"} fontSize={"7xl"} color={"green.400"}>
              Valor Total {formatBRL(Number(data.pedido.valor_total))}
            </Text>
          </Box>
          <Box bg={"#f4f4f4"} p={"2"} display={"flex"} flexDir={"column"} gap={2}>
            <Text fontSize={"lg"} fontWeight={"bold"}>
              CARTÃO DE EMBARQUE
            </Text>
            {(["ida", "volta"] as const).map((contexto) => {
              const itens = data.bilhetes.filter((item) => item.contexto === contexto);
              if (itens.length === 0) {
                return null;
              }

              const label = contexto === "ida" ? "VIAGEM DE IDA" : "VIAGEM DE VOLTA";

              return (
                <Box key={`cartao-${contexto}`} mb={2}>
                  <Text fontWeight={"bold"} textTransform={"uppercase"} mb={2}>
                    {label}
                  </Text>
                  {itens.map((item) => {
                    const linkOk = !!item?.comprovante_bpe?.trim();
                    const enderecoEmbarque = item.endereco_embarque || item.cidade_origem?.endereco_embarque;
                    const isBilheteComDocumentoBloqueado = [
                      BilheteStatusCode.Reservado,
                      BilheteStatusCode.Expirado,
                      BilheteStatusCode.Invalido,
                      BilheteStatusCode.EstornoPendente,
                    ].includes(item.status as BilheteStatusCode);

                    return (
                      <Flex
                        key={item.id}
                        direction="column"
                        gap={2}
                        py={2}
                        px={4}
                        borderWidth={1}
                        borderRadius="md"
                        borderColor="gray.100"
                        shadow="md"
                      >
                        {item.viacao.alerta !== null && (
                          <Alert status="error">
                            <AlertIcon />
                            <AlertTitle>{item.viacao.alerta.title}</AlertTitle>
                            <AlertDescription>{item.viacao.alerta.mensagem}</AlertDescription>
                          </Alert>
                        )}

                        {isRs(item.cidade_origem.nome, item.cidade_destino.nome) && (
                          <Alert status="error">
                            <AlertIcon />
                            <AlertTitle>Atenção</AlertTitle>
                            <AlertDescription>
                              Devido a essa viagem ser uma viagem intermunicipal dentro do estado do Rio Grande do Sul,
                              é obrigatório a impressão do BPE, bilhete de passagem eletrônico
                            </AlertDescription>
                          </Alert>
                        )}

                        {enderecoEmbarque && (
                          <Box bg="yellow.50" borderWidth={1} borderColor="yellow.200" borderRadius="md" px={3} py={2}>
                            <Text fontWeight="bold" fontSize="sm" color="gray.700">
                              Endereço de embarque
                            </Text>
                            <Text fontSize="sm" color="gray.700">
                              {enderecoEmbarque}
                            </Text>
                          </Box>
                        )}

                        {isRs(item.cidade_origem.nome, item.cidade_destino.nome) && !isBilheteComDocumentoBloqueado ? (
                          <Button
                            colorScheme="red"
                            leftIcon={<IoTicketSharp />}
                            as="a"
                            href={
                              linkOk
                                ? item.comprovante_bpe
                                : `${import.meta.env.VITE_BASE_URL_BACKEND_API}/passagens/${id}/voucher`
                            }
                            target="_blank"
                            rel="noopener"
                          >
                            {linkOk ? "Baixar BPE - " : "Baixar Voucher - "}
                            <Text as="span" mx={2} textTransform="uppercase" fontSize="lg">
                              {item.passageiro.nome} - {item.assento} - {item.cidade_origem.nome} {item.cidade_destino.nome}
                            </Text>
                          </Button>
                        ) : (
                          <Flex alignItems="center" gap={4}>
                            {!isBilheteComDocumentoBloqueado &&
                              item?.viacao?.documento_preferencial?.includes(DocumentoPreferencial.BPe) &&
                              linkOk && (
                                <Button
                                  colorScheme="red"
                                  leftIcon={<IoTicketSharp />}
                                  as="a"
                                  href={
                                    linkOk
                                      ? item.comprovante_bpe
                                      : `${import.meta.env.VITE_BASE_URL_BACKEND_API}/passagens/${id}/voucher`
                                  }
                                  target="_blank"
                                  rel="noopener"
                                >
                                  Baixar BPE
                                </Button>
                            )}

                            {!isBilheteComDocumentoBloqueado &&
                              item?.viacao?.documento_preferencial?.includes(DocumentoPreferencial.CartaoEmbarque) &&
                              linkOk && (
                                <Button
                                  colorScheme="red"
                                  leftIcon={<HiTicket />}
                                  onClick={() => {abrirCartaoPdf(Number(item.id));}}
                                >
                                  Baixar Comprovante de Embarque
                                </Button>
                            )}

                            {!isBilheteComDocumentoBloqueado && (
                              (
                                !linkOk ||
                                item?.viacao?.documento_preferencial?.includes(DocumentoPreferencial.Voucher)
                              ) && (
                                <Button
                                  colorScheme="red"
                                  leftIcon={<MdFileDownload />}
                                  onClick={() => abrirVoucherPdf(Number(id))}
                                >
                                  Baixar Voucher
                                </Button>
                              )
                            )}

                            <Text textTransform="uppercase" fontSize="lg">
                              {item.passageiro.nome} - {item.assento} - {item.cidade_origem.nome} {item.cidade_destino.nome}
                            </Text>
                          </Flex>
                        )}
                      </Flex>
                    );
                  })}
                </Box>
              );
            })}

          </Box>
          <Flex justifyContent={'center'}>
            <Button onClick={() => navigate('/')} width={'320px'} height={'50px'} fontSize={'2xl'} fontWeight={'bold'} leftIcon={<HiArrowUturnLeft size={'40px'} />} bgColor={'yellow.400'}>Voltar ao Inicio</Button>
          </Flex>
        </Box>
      </Flex>
    </Box>
  );
}
